<template>
  <div>
    <div v-if="groups.length > 0" class="meetings-list">
      <section v-for="group in groups" :key="group.key" class="group">
        <div class="group-header">{{ group.title }}</div>
        <MeetingItem
          v-for="meeting in group.meetings"
          :key="meeting.id"
          :meeting="meeting"
          class="meeting-item"
        />
      </section>
    </div>
    <div v-else class="empty-state">
      <p>
        {{
          searchQuery
            ? "No meetings match your search"
            : "Notes you take on calendar events will show up here"
        }}
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { addDays, startOfDay } from "date-fns";
import { computed, defineComponent, shallowRef, toRefs, watch } from "vue";

import type { EventId, FullEvent, ReleasableRef } from "@ctrl/controller-rpc";

import * as inject from "@/components/inject";
import {
  groupMeetings,
  type Meeting,
  type MeetingGroup,
} from "@/components/meetings/meetingGroups";
import MeetingItem from "@/components/meetings/MeetingItem.vue";
import { UseSettings } from "@/components/settings/controller/settings";
import { bindAsync, useReleaser } from "@/ref";

/** How many future meetings the Upcoming section shows. */
const UPCOMING_COUNT = 3;

/** How many days ahead the Upcoming section scans for meetings. */
const UPCOMING_WINDOW_DAYS = 30;

export default defineComponent({
  name: "MeetingsList",
  components: {
    MeetingItem,
  },
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },
  emits: {
    "update:total": (_count: number) => true,
  },
  async setup(props, { emit }) {
    const controller = inject.controller();
    const clock = inject.clock();
    const calendars = inject.calendars();
    const { isWeekStartOnSunday } = UseSettings.getInstance();
    const { searchQuery } = toRefs(props);
    const releaser = useReleaser();

    // Event ids with notes, most recent first, kept up to date by the
    // controller.
    const eventIds = await releaser.add(controller.event.withNotes());

    // Event ids of the next days (for the Upcoming section), re-queried when
    // the day flips.
    const dayStart = computed(() => startOfDay(clock.value).getTime());
    const upcomingIds = await releaser.add(
      bindAsync(dayStart, (start) =>
        Promise.resolve(
          controller.event.days(
            Array.from({ length: UPCOMING_WINDOW_DAYS }, (_, i) =>
              addDays(new Date(start), i)
            ),
            "with-all-day",
            true
          )
        )
      )
    );

    // The events behind the ids, loaded once each and released with the
    // component; ids added while the screen is open are loaded by the watcher
    // below.
    //
    // Every noted event is loaded eagerly, on purpose: the data is local
    // (offline-first controller, no network round-trip), so even years of
    // meetings load fast — and lazy loading would drag in real machinery
    // (a paged window, a scroll sentinel, searching beyond the loaded pages,
    // keeping the header count exact) for little gain. If this ever gets
    // slow on huge accounts, prefer a limit on the controller query over
    // frontend paging.
    const eventRefs = shallowRef(
      new Map<EventId, ReleasableRef<FullEvent | null>>()
    );
    const loadEvents = async (ids: EventId[]) => {
      const missing = ids.filter((id) => !eventRefs.value.has(id));
      if (missing.length === 0) return;
      const loaded = await Promise.all(
        missing.map(
          async (id) =>
            [id, await releaser.add(controller.event.get(id))] as const
        )
      );
      const next = new Map(eventRefs.value);
      for (const [id, eventRef] of loaded) {
        next.set(id, eventRef);
      }
      eventRefs.value = next;
    };
    releaser.defer(
      watch(eventIds, (ids) => {
        if (ids) void loadEvents(ids);
      })
    );
    releaser.defer(
      watch(upcomingIds, (ids) => {
        if (ids) void loadEvents(ids);
      })
    );
    await loadEvents([...(eventIds.value ?? []), ...(upcomingIds.value ?? [])]);

    // Past meetings: every event with notes that already started, regardless
    // of activity. Events still to come belong to the Upcoming section
    // instead (and only show there when active).
    const meetings = computed<Meeting[]>(() => {
      const now = clock.value;
      const result: Meeting[] = [];
      for (const id of eventIds.value ?? []) {
        const event = eventRefs.value.get(id)?.value;
        if (!event) continue;
        if (event.start > now) continue;
        result.push({
          id,
          title: event.title,
          start: event.start,
          end: event.end,
          allDay: event.allDay,
          calendarColor: calendars.value?.byId.get(event.calendar)?.color,
        });
      }
      return result;
    });

    // The next meetings to come — restricted to the events the agenda shows:
    // active, not archived, no drafts, and not from a disabled calendar —
    // limited to the scanned window and the section cap.
    const upcomingMeetings = computed<Meeting[]>(() => {
      const now = clock.value;
      const seen = new Set<EventId>();
      const result: Meeting[] = [];
      for (const id of upcomingIds.value ?? []) {
        if (seen.has(id)) continue;
        seen.add(id);
        const event = eventRefs.value.get(id)?.value;
        if (!event) continue;
        if (event.start <= now) continue;
        if (!event.active || event.isArchived || event.draft) continue;
        if (calendars.value?.byId.get(event.calendar)?.disabled) continue;
        result.push({
          id,
          title: event.title,
          start: event.start,
          end: event.end,
          allDay: event.allDay,
          calendarColor: calendars.value?.byId.get(event.calendar)?.color,
        });
      }
      // The closest meetings win the cap, displayed latest first so the
      // section reads antechronologically, like the rest of the list.
      result.sort((a, b) => a.start.getTime() - b.start.getTime());
      return result.slice(0, UPCOMING_COUNT).reverse();
    });

    const matchesQuery = (meetings: Meeting[]): Meeting[] => {
      const query = searchQuery.value.trim().toLowerCase();
      if (!query) return meetings;
      return meetings.filter((meeting) =>
        meeting.title.toLowerCase().includes(query)
      );
    };
    const filteredMeetings = computed<Meeting[]>(() =>
      matchesQuery(meetings.value)
    );
    const filteredUpcoming = computed<Meeting[]>(() =>
      matchesQuery(upcomingMeetings.value)
    );

    const groups = computed<MeetingGroup[]>(() => {
      const past = groupMeetings(
        filteredMeetings.value,
        clock.value,
        isWeekStartOnSunday.value
      );
      const upcoming = filteredUpcoming.value;
      if (upcoming.length === 0) return past;
      return [
        { key: "upcoming", title: "Upcoming", meetings: upcoming },
        ...past,
      ];
    });

    // The header total: exactly the rows displayed.
    releaser.defer(
      watch(
        [filteredMeetings, filteredUpcoming],
        ([past, upcoming]) => {
          emit("update:total", past.length + upcoming.length);
        },
        { immediate: true }
      )
    );

    return {
      groups,
    };
  },
});
</script>

<style scoped lang="scss">
.meetings-list {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  // 64px page inset minus the row's 16px padding, so titles stay aligned
  // with the header while the hover extends past the icon.
  padding-inline: 48px;

  .group {
    display: flex;
    flex-direction: column;
    row-gap: 4px;

    .group-header {
      @extend %font-h3;
      padding-inline: 16px;
      margin-bottom: 8px;
    }
  }
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px;
  @extend %font-body-regular;
  @include themed() {
    color: t($grey-40);
  }
}
</style>
